import React, { useState, useEffect } from 'react';
import { FirePreventionInstructionItem, FirePreventionInstructionItemFormData, Employee, FirePreventionInstructionType } from '../../types/models';
import { FirePreventionInstructionItemService } from '../../services/firePreventionInstructionItemService';
import { EmployeeService } from '../../services/employeeService';
import { EmployeeAutocomplete } from '../EmployeeAutocomplete';
import { formatDateForInput } from '../../utils/dateUtils';

interface FirePreventionInstructionItemFormProps {
  instruction?: FirePreventionInstructionItem;
  employeeId?: number;
  onSuccess: (createdItem?: FirePreventionInstructionItem) => void;
  onCancel: () => void;
}

export const FirePreventionInstructionItemForm: React.FC<FirePreventionInstructionItemFormProps> = ({
  instruction,
  employeeId,
  onSuccess,
  onCancel
}) => {
  const [formData, setFormData] = useState<FirePreventionInstructionItemFormData>({
    employeeId: instruction?.employeeId || employeeId || 0,
    responsibleEmployeeId: instruction?.responsibleEmployeeId || 0,
    date: instruction?.date ? new Date(instruction.date).toISOString().split('T')[0] : '',
    type: instruction?.type || FirePreventionInstructionType.Init,
  });

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Загружаем всех сотрудников
      const employeesResponse = await EmployeeService.getAll();

      if (employeesResponse.success) {
        setEmployees(employeesResponse.data);
      }
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let response;
      if (instruction) {
        // Обновление
        response = await FirePreventionInstructionItemService.update(instruction.id, { ...formData, id: instruction.id });
      } else {
        // Создание
        response = await FirePreventionInstructionItemService.create(formData);
      }

      if (response.success) {
        // Для создания передаем созданный элемент, для обновления - ничего
        onSuccess(instruction ? undefined : (response.data as FirePreventionInstructionItem));
      } else {
        setError(response.message || 'Ошибка сохранения');
      }
    } catch (err) {
      setError('Произошла ошибка при сохранении');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('Id') ? Number(value) : value
    }));
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        style={{ maxWidth: '800px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card" style={{ margin: 0, boxShadow: 'none' }}>
          <h3>{instruction ? 'Редактирование противопожарного инструктажа' : 'Создание противопожарного инструктажа'}</h3>

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Сотрудник *</label>
            <EmployeeAutocomplete
              value={formData.employeeId}
              onChange={(employeeId) => setFormData(prev => ({ ...prev, employeeId: employeeId || 0 }))}
              placeholder="Начните вводить ФИО сотрудника..."
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Ответственный сотрудник *</label>
            <EmployeeAutocomplete
              value={formData.responsibleEmployeeId}
              onChange={(employeeId) => setFormData(prev => ({ ...prev, responsibleEmployeeId: employeeId || 0 }))}
              placeholder="Начните вводить ФИО ответственного..."
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Дата проведения *</label>
            <input
              type="date"
              name="date"
              className="form-input"
              value={formatDateForInput(formData.date)}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Вид проводимого инструктажа *</label>
            <select
              name="type"
              className="form-input"
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: Number(e.target.value) as FirePreventionInstructionType }))}
              required
            >
              <option value={FirePreventionInstructionType.Init}>Вводный, Первичный</option>
              <option value={FirePreventionInstructionType.First}>Первичный</option>
              <option value={FirePreventionInstructionType.Repeat}>Повторный</option>
            </select>
          </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Сохранение...' : (instruction ? 'Сохранить' : 'Создать')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Отмена
          </button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};